/**
 * Minimal Markdown → HTML converter for chat/assistant messages.
 *
 * Casey (the AI Concierge) replies in Markdown; this renders the common
 * constructs it emits — headings, bold/italic, inline & fenced code, links,
 * ordered/unordered lists, blockquotes, and paragraphs/line breaks — without
 * pulling in a full Markdown dependency.
 *
 * Security: the input is HTML-escaped FIRST, so no raw HTML in the source can
 * survive. The output is still passed through DOMPurify at the render site
 * (SafeHtml) as defense in depth. This is intentionally a small subset, not a
 * spec-complete parser.
 */

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/** Inline formatting: code, bold, italic, links. Operates on escaped text. */
function inline(text: string): string {
  // Inline code first so its contents aren't further formatted. Use a private-
  // use-area sentinel that won't appear in normal text and isn't a control char.
  const SENTINEL = '\uE000'
  const codeSpans: string[] = []
  let out = text.replace(/`([^`]+)`/g, (_m, code) => {
    codeSpans.push(`<code>${code}</code>`)
    return `${SENTINEL}${codeSpans.length - 1}${SENTINEL}`
  })

  // Links [text](url) — only http(s)/mailto; anything else renders as plain text.
  out = out.replace(
    /\[([^\]]+)\]\(([^)\s]+)\)/g,
    (_m, label, href: string) => {
      if (/^(https?:|mailto:)/i.test(href)) {
        return `<a href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>`
      }
      return label
    },
  )

  // Bold then italic. **bold**, __bold__, *italic*, _italic_.
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  out = out.replace(/__([^_]+)__/g, '<strong>$1</strong>')
  out = out.replace(/\*([^*]+)\*/g, '<em>$1</em>')
  out = out.replace(/(^|[^_])_([^_]+)_(?!_)/g, '$1<em>$2</em>')

  // Restore inline code spans.
  out = out.replace(
    new RegExp(`${SENTINEL}(\\d+)${SENTINEL}`, 'g'),
    (_m, i) => codeSpans[Number(i)],
  )
  return out
}

export function renderMarkdown(src: string): string {
  const escaped = escapeHtml(src)
  const lines = escaped.split('\n')
  const html: string[] = []

  let i = 0
  let inList: 'ul' | 'ol' | null = null
  let inCode = false
  let codeLines: string[] = []

  const closeList = () => {
    if (inList) {
      html.push(`</${inList}>`)
      inList = null
    }
  }

  while (i < lines.length) {
    const line = lines[i]

    // Fenced code blocks ```
    if (/^\s*```/.test(line)) {
      if (inCode) {
        html.push(`<pre><code>${codeLines.join('\n')}</code></pre>`)
        inCode = false
        codeLines = []
      } else {
        closeList()
        inCode = true
      }
      i++
      continue
    }
    if (inCode) {
      codeLines.push(line)
      i++
      continue
    }

    // Blank line → paragraph break / list end.
    if (line.trim() === '') {
      closeList()
      i++
      continue
    }

    // Headings # .. ######
    const heading = line.match(/^(#{1,6})\s+(.*)$/)
    if (heading) {
      closeList()
      const level = heading[1].length
      html.push(`<h${level}>${inline(heading[2])}</h${level}>`)
      i++
      continue
    }

    // Blockquote. Note: the source was HTML-escaped first, so a leading ">" is
    // now "&gt;" — match that.
    if (/^\s*&gt;\s?/.test(line)) {
      closeList()
      html.push(
        `<blockquote>${inline(line.replace(/^\s*&gt;\s?/, ''))}</blockquote>`,
      )
      i++
      continue
    }

    // Unordered list item - or *
    const ul = line.match(/^\s*[-*]\s+(.*)$/)
    if (ul) {
      if (inList !== 'ul') {
        closeList()
        html.push('<ul>')
        inList = 'ul'
      }
      html.push(`<li>${inline(ul[1])}</li>`)
      i++
      continue
    }

    // Ordered list item 1.
    const ol = line.match(/^\s*\d+\.\s+(.*)$/)
    if (ol) {
      if (inList !== 'ol') {
        closeList()
        html.push('<ol>')
        inList = 'ol'
      }
      html.push(`<li>${inline(ol[1])}</li>`)
      i++
      continue
    }

    // Paragraph — merge consecutive non-empty, non-special lines with <br/>.
    closeList()
    const para: string[] = [line]
    let j = i + 1
    while (
      j < lines.length &&
      lines[j].trim() !== '' &&
      !/^\s*```/.test(lines[j]) &&
      !/^(#{1,6})\s+/.test(lines[j]) &&
      !/^\s*&gt;\s?/.test(lines[j]) &&
      !/^\s*[-*]\s+/.test(lines[j]) &&
      !/^\s*\d+\.\s+/.test(lines[j])
    ) {
      para.push(lines[j])
      j++
    }
    html.push(`<p>${para.map(inline).join('<br/>')}</p>`)
    i = j
  }

  // Close any open block at EOF.
  if (inCode) html.push(`<pre><code>${codeLines.join('\n')}</code></pre>`)
  closeList()

  return html.join('\n')
}
